// ===================================================================
// services/friendsService.ts
// Gestion des amis et profils utilisateurs (Supabase).
// Tables requises : user_profiles, user_follows
// ===================================================================
import { supabase } from './supabaseAudioService';
import { SESSION_DATE } from '../hooks/useDailyStreak';

export interface UserProfile {
  id: string;
  username: string | null;
  display_name: string | null;
  avatar_url: string | null;
  total_xp: number;
  lifetime_xp?: number | null; // XP cumulée toutes langues (migration 2026-10-24)
  daily_streak: number;
  last_active: string;
  is_public: boolean;
  activity_history?: string[] | null; // dates YYYY-MM-DD des jours actifs (max 30)
  // Enrichi côté client via user_progress
  learning_language?: string | null;  // code langue le plus récent ('wf', 'sw', …)
  learning_language_xp?: number | null; // XP dans cette langue
}

const LANGUAGE_INFO: Record<string, { flag: string; name: string }> = {
  wf: { flag: '🇸🇳', name: 'Wolof' },
  ba: { flag: '🇲🇱', name: 'Bambara' },
  la: { flag: '🇨🇩', name: 'Lingala' },
  ff: { flag: '🇬🇳', name: 'Peul' },
  co: { flag: '🇪🇬', name: 'Copte' },
  sw: { flag: '🇰🇪', name: 'Swahili' },
  yo: { flag: '🇳🇬', name: 'Yoruba' },
};

/**
 * XP affichée et classée : le cumul toutes langues quand il existe, sinon
 * (migration pas encore passée) l'ancien total de la langue en cours.
 */
export const displayXP = (p: Pick<UserProfile, 'total_xp' | 'lifetime_xp'>): number =>
  p.lifetime_xp ?? p.total_xp ?? 0;

export const getLanguageInfo = (code: string | null | undefined) =>
  code ? (LANGUAGE_INFO[code] ?? null) : null;

/** Enrichit une liste de profils avec la langue la plus récemment active */
const enrichWithProgress = async (profiles: UserProfile[]): Promise<UserProfile[]> => {
  if (profiles.length === 0) return profiles;
  const ids = profiles.map((p) => p.id);

  const { data } = await supabase
    .from('user_progress')
    .select('user_id, language_code, total_xp, updated_at')
    .in('user_id', ids)
    .order('updated_at', { ascending: false });

  if (!data) return profiles;

  // Pour chaque utilisateur, garder la ligne la plus récente (déjà triée desc)
  const latestByUser = new Map<string, { language_code: string; total_xp: number }>();
  for (const row of data as any[]) {
    if (!latestByUser.has(row.user_id)) {
      latestByUser.set(row.user_id, { language_code: row.language_code, total_xp: row.total_xp ?? 0 });
    }
  }

  return profiles.map((p) => {
    const prog = latestByUser.get(p.id);
    return prog
      ? { ...p, learning_language: prog.language_code, learning_language_xp: prog.total_xp }
      : p;
  });
};

// Crée ou met à jour le profil de l'utilisateur connecté
export const upsertProfile = async (
  userId: string,
  data: Partial<Omit<UserProfile, 'id'>>
): Promise<void> => {
  const { error } = await supabase
    .from('user_profiles')
    .upsert({ id: userId, ...data }, { onConflict: 'id' });
  if (error) throw error;
};

// Synchronise le total XP dans le profil Supabase
export const syncXP = async (userId: string, totalXP: number): Promise<void> => {
  const { error } = await supabase
    .from('user_profiles')
    .upsert(
      { id: userId, total_xp: totalXP, last_active: new Date().toISOString().split('T')[0] },
      { onConflict: 'id' }
    );
  if (error) throw error;
};

// Synchronise le streak quotidien et l'historique d'activité vers Supabase
// Nécessite la colonne activity_history (jsonb) dans user_profiles
export const syncStreak = async (
  userId: string,
  dailyStreak: number,
  activityHistory?: string[]
): Promise<void> => {
  // Date de session (comme le streak), pas la date réelle : une session qui
  // passe minuit reste attribuée au jour où elle a commencé.
  const today = SESSION_DATE;

  // 1. Sync daily_streak (colonne existante)
  const { error: e1 } = await supabase
    .from('user_profiles')
    .upsert(
      { id: userId, daily_streak: dailyStreak, last_active: today },
      { onConflict: 'id' }
    );
  if (e1) throw e1;

  // 2. Sync activity_history si fourni (colonne jsonb — silent fail si absente)
  if (activityHistory && activityHistory.length > 0) {
    // Fusion avec l'historique déjà en ligne : sinon un appareil aux données
    // anciennes effaçait les jours enregistrés depuis un autre appareil.
    let merged = activityHistory;
    try {
      const { data } = await supabase
        .from('user_profiles')
        .select('activity_history')
        .eq('id', userId)
        .maybeSingle();
      const remote = Array.isArray(data?.activity_history) ? (data!.activity_history as string[]) : [];
      merged = Array.from(new Set([...remote, ...activityHistory])).sort().slice(-30);
    } catch { /* lecture impossible : on garde l'historique local */ }
    activityHistory = merged;
    await supabase
      .from('user_profiles')
      .upsert(
        { id: userId, activity_history: activityHistory },
        { onConflict: 'id' }
      )
      .then(({ error }) => {

      });
  }
};

// Recherche par username ou display_name (ilike, insensible à la casse)
// Supporte le préfixe "@" pour chercher par username uniquement
export const searchUsers = async (query: string): Promise<UserProfile[]> => {
  if (!query || query.trim().length < 2) return [];

  const raw     = query.trim();
  const byAt    = raw.startsWith('@');           // "@nom" → username uniquement
  const trimmed = raw.replace(/^@/, '').toLowerCase();

  let q = supabase.from('user_profiles').select('*');

  if (byAt) {
    // Recherche username uniquement, priorité aux correspondances en début de mot
    q = q.ilike('username', `%${trimmed}%`);
  } else {
    // Recherche username OU display_name
    q = q.or(`username.ilike.%${trimmed}%,display_name.ilike.%${trimmed}%`);
  }

  const { data, error } = await q.limit(8);
  if (error) throw error;

  // Tri : correspondances en début de chaîne en premier
  const results = (data ?? []) as UserProfile[];
  return results.sort((a, b) => {
    const aName  = (a.username ?? a.display_name ?? '').toLowerCase();
    const bName  = (b.username ?? b.display_name ?? '').toLowerCase();
    const aStart = aName.startsWith(trimmed) ? 0 : 1;
    const bStart = bName.startsWith(trimmed) ? 0 : 1;
    return aStart - bStart;
  });
};

// Suivre un utilisateur
export const followUser = async (followerId: string, followingId: string): Promise<void> => {
  const { error } = await supabase
    .from('user_follows')
    .insert({ follower_id: followerId, following_id: followingId });
  if (error && error.code !== '23505') throw error; // ignore doublon
};

// Ne plus suivre
export const unfollowUser = async (followerId: string, followingId: string): Promise<void> => {
  const { error } = await supabase
    .from('user_follows')
    .delete()
    .match({ follower_id: followerId, following_id: followingId });
  if (error) throw error;
};

// Liste des gens que je suis (enrichie avec la langue active)
export const getFollowing = async (userId: string): Promise<UserProfile[]> => {
  const { data: follows, error: e1 } = await supabase
    .from('user_follows')
    .select('following_id')
    .eq('follower_id', userId);

  if (e1) throw e1;
  if (!follows || follows.length === 0) return [];

  const followingIds = follows.map((f: any) => f.following_id);
  const { data, error: e2 } = await supabase
    .from('user_profiles')
    .select('*')
    .in('id', followingIds);

  if (e2) throw e2;
  const profiles = (data ?? []) as UserProfile[];
  return enrichWithProgress(profiles);
};

// Vérifie si je suis déjà quelqu'un
export const isFollowing = async (followerId: string, followingId: string): Promise<boolean> => {
  const { count, error } = await supabase
    .from('user_follows')
    .select('id', { count: 'exact', head: true })
    .match({ follower_id: followerId, following_id: followingId });

  if (error) throw error;
  return (count ?? 0) > 0;
};

// Leaderboard : moi + personnes suivies, triés par XP décroissant (enrichi avec langue active)
export const getLeaderboard = async (userId: string): Promise<UserProfile[]> => {
  // 1. Récupère les IDs des gens que je suis
  const { data: follows, error: e1 } = await supabase
    .from('user_follows')
    .select('following_id')
    .eq('follower_id', userId);

  if (e1) throw e1;

  const followingIds = (follows ?? []).map((f: any) => f.following_id);
  const ids = [userId, ...followingIds];

  // 2. Récupère leurs profils
  const { data, error: e2 } = await supabase
    .from('user_profiles')
    .select('*')
    .in('id', ids);

  if (e2) throw e2;
  // Tri côté app : la colonne lifetime_xp n'existe qu'après la migration
  const profiles = ((data ?? []) as UserProfile[]).sort((a, b) => displayXP(b) - displayXP(a));
  return enrichWithProgress(profiles);
};

/**
 * XP gagnée cette semaine (depuis lundi 00:00 UTC) par moi et les personnes
 * que je suis — fonction Supabase weekly_xp_friends (migration
 * 2026-10-24_weekly_leaderboard.sql). Renvoie null si elle n'est pas
 * disponible : l'écran garde alors le seul classement « depuis le début ».
 */
export const getWeeklyXP = async (): Promise<Map<string, number> | null> => {
  try {
    const { data, error } = await supabase.rpc('weekly_xp_friends');
    if (error || !Array.isArray(data)) return null;
    return new Map(data.map((r: any) => [r.user_id as string, Number(r.weekly_xp) || 0]));
  } catch {
    return null;
  }
};

/** Temps restant avant la remise à zéro (lundi 00:00 UTC). */
export const timeUntilWeekReset = (now: Date = new Date()): { days: number; hours: number } => {
  const next = new Date(Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate()));
  const daysToMonday = (8 - next.getUTCDay()) % 7 || 7; // dimanche=0 → 1 j ; lundi → 7 j
  next.setUTCDate(next.getUTCDate() + daysToMonday);
  const totalHours = Math.max(0, Math.floor((next.getTime() - now.getTime()) / 3_600_000));
  return { days: Math.floor(totalHours / 24), hours: totalHours % 24 };
};

// Récupère son propre profil
export const getMyProfile = async (userId: string): Promise<UserProfile | null> => {
  const { data, error } = await supabase
    .from('user_profiles')
    .select('*')
    .eq('id', userId)
    .single();

  if (error && error.code !== 'PGRST116') throw error;
  return (data ?? null) as UserProfile | null;
};
